import { useEffect, useMemo, useRef, useState } from "react";
import logoURFX from "@/assets/Navbar/logo.png";

type CarouselCard = {
    name: string;
    amount: string;
    paidAt: string;
    account: string;
    highlighted?: boolean;

};

function mod(n: number, m: number) {
    return ((n % m) + m) % m;
}

function clamp(n: number, min: number, max: number) {
    return Math.min(max, Math.max(min, n));
}

export default function Hero3dCrousel() {
    const cards: CarouselCard[] = useMemo(
        () => [
            {
                name: "Lucas Moretti",
                account: "One8x2a3b712e7c75a70350ab4d3f...",
                amount: "$17,891.19",
                paidAt: "21 Days Ago (Jul-15-2025 10:14:14 PM UTC)",
            },
            {
                name: "Emily Chen",
                account: "One8x2a3b903a30d54b044d5b7ba...",
                amount: "$22,134.10",
                paidAt: "21 Days Ago (Jul-16-2025 12:31:35 AM UTC)",
                highlighted: true,
            },
            {
                name: "Omar Rahmani",
                account: "0x74c0e43c79eec1cc03944b67eec...",
                amount: "$22,134.10",
                paidAt: "36 Days Ago (Jun-30-2025 05:57:11 PM UTC)",
            },
        ],
        []
    );

    const step = 360 / cards.length;
    const [rotation, setRotation] = useState(0); // degrees
    const rafRef = useRef<number | null>(null);
    const lastTsRef = useRef<number | null>(null);
    const [layout, setLayout] = useState<{
        rotateY: number;
        radius: number;
    }>({
        rotateY: 28,
        radius: 360,
    });

    // Responsive radius for the 3D arc
    useEffect(() => {
        const update = () => {
            const w = window.innerWidth;
            if (w < 640) {
                setLayout({
                    rotateY: 26,
                    radius: 230,
                });
            } else if (w < 1024) {
                setLayout({
                    rotateY: 28,
                    radius: 300,
                });
            } else {
                setLayout({
                    rotateY: 30,
                    radius: 380,
                });
            }
        };
        update();
        window.addEventListener("resize", update);
        return () => window.removeEventListener("resize", update);
    }, []);

    // Autoplay: continuous rotation (real 3D rotate)
    useEffect(() => {
        const prefersReducedMotion =
            typeof window !== "undefined" &&
            window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

        if (prefersReducedMotion) return;
        if (cards.length <= 1) return;

        const speedDegPerSec = 26; // closer to design: visible rotation

        const loop = (ts: number) => {
            const last = lastTsRef.current ?? ts;
            lastTsRef.current = ts;
            const dt = Math.min(0.05, (ts - last) / 1000);

            setRotation((prev) => prev - speedDegPerSec * dt);
            rafRef.current = window.requestAnimationFrame(loop);
        };

        rafRef.current = window.requestAnimationFrame(loop);

        return () => {
            if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
            lastTsRef.current = null;
        };
    }, [cards.length]);

    const GLASS_CARD =
        "backdrop-blur-[28px] rounded-[22px] border border-white/35 bg-[linear-gradient(180deg,rgba(255,255,255,0.04)_0%,rgba(255,255,255,0.02)_100%)]";

    return (
        <div className="relative w-full">
            <div
                className="relative mx-auto h-[360px] md:h-[420px] w-full max-w-[840px]"
            // style={{ perspective: "1200px" }}
            >
                <div
                    className="absolute inset-0"
                    style={{
                        transformStyle: "preserve-3d",
                        transform: `rotateY(${rotation}deg)`,
                        transition: "transform 0ms",
                    }}
                >
                    {cards.map((card, idx) => {
                        const localAngle = idx * step;
                        const worldAngle = rotation + localAngle;
                        const normalizedWorldAngle = mod(worldAngle + 180, 360) - 180; // [-180..180]
                        const abs = Math.abs(normalizedWorldAngle);

                        // Only show the 3 cards around the front; hide the rest.
                        const show = abs <= step * 1.1;

                        // 0 = side card, 1 = front card — drives smooth styling (no active snap)
                        const frontness = clamp(1 - abs / step, 0, 1);

                        // Smooth counter-rotation while orbiting (no 0 ↔ ±rotateY jump)
                        const faceRotate = clamp(
                            -normalizedWorldAngle * (layout.rotateY / step),
                            -layout.rotateY,
                            layout.rotateY,
                        );

                        const scale = 0.86 + 0.14 * frontness;
                        const cardSizeClass =
                            "w-[250px] h-[250px] md:w-[360px] md:h-[360px] md:max-h-[460px]";

                        return (
                            <div
                                key={`${idx}-${card.name}`}
                                className="absolute left-1/2 top-1/2 "
                                style={{
                                    transformStyle: "preserve-3d",
                                    transform: `translate(-50%, -50%) rotateY(${localAngle}deg) translateZ(${layout.radius}px) rotateY(${faceRotate}deg) scale(${scale})`,
                                    opacity: show ? 0.45 + 0.55 * frontness : 0,
                                    filter: show
                                        ? `blur(${(1 - frontness) * 0.6}px) saturate(${0.88 + 0.12 * frontness})`
                                        : "none",
                                    visibility: show ? "visible" : "hidden",
                                    pointerEvents: "none",
                                }}
                            >
                                <div className={`relative z-30 overflow-hidden ${cardSizeClass}`}>
                                    <div
                                        className={[
                                            "absolute inset-0 transition-opacity duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
                                            GLASS_CARD,
                                        ].join(" ")}
                                        style={{ opacity: 1 - frontness * 0.35 }}
                                    />
                                    <div
                                        className={[
                                            "absolute inset-0 transition-opacity duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
                                            "card-gradient",
                                        ].join(" ")}
                                        style={{ opacity: frontness }}
                                    />
                                    <div className="relative flex h-full w-full flex-col p-4 md:p-6 text-center text-white font-nexa">
                                        <div className="flex items-center justify-center gap-1 font-nexa text-[13px] font-semibold text-white">
                                            <img src={logoURFX} alt="URFX" className="w-20 h-6" />
                                        </div>
                                        <p className="mt-2 text-sm md:mt-3 md:text-base text-white/80 relative">
                                            We Want To Congratulate
                                        </p>
                                        <h3
                                            className="mt-1 text-xl md:text-[28px] font-bold tracking-[-0.04em] transition-colors duration-500 relative z-30"
                                            style={{
                                                color: `rgba(211, 237, 5, ${0.35 + 0.9 * frontness})`,
                                            }}
                                        >
                                            {card.name}
                                        </h3>
                                        <p className="mx-auto py-1 text-center text-sm text-white/80 relative z-30">
                                            {card.account}
                                        </p>

                                        <p className="mt-2 text-xs md:text-sm text-white/80">
                                            For Their Hard-Earned Payout Of:
                                        </p>

                                        <div className="relative mt-2 sm:mt-3">
                                            <div
                                                className="rounded-[10px] border border-white/20 bg-[#151515]/70 px-3 py-1.5 text-center font-creato font-semibold tracking-[-0.04em] text-[16px] text-white/70 transition-opacity duration-500 sm:text-[36px] md:px-4 md:py-2"
                                                style={{ opacity: 1 - frontness }}
                                            >
                                                {card.amount}
                                            </div>
                                            <div
                                                className="absolute inset-0 rounded-[10px] px-3 py-1.5 text-center font-creato text-2xl font-semibold tracking-[-0.04em] text-white transition-opacity duration-500 md:px-4 md:py-2 md:text-[40px] card-gradient-no-shadow"
                                                style={{ opacity: frontness }}
                                            >
                                                {card.amount}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="mt-2 text-center bg-gradient-to-r from-[#D6E242] via-[#9DDF78] to-[#79DDC7] bg-clip-text font-normal text-transparent text-xs"
                                        >
                                            View Transaction Details
                                        </button>

                                        <div className="mt-auto flex items-start justify-start gap-2 pt-2 text-left sm:pt-4">
                                            <p className="w-[70px] text-[10px] text-white/30 md:w-[90px] md:text-xs">
                                                Pay Out On:
                                            </p>
                                            <p className="text-[10px] leading-4 text-white/40 md:text-xs">
                                                <span
                                                    className="transition-colors duration-500"
                                                    style={{
                                                        color: `rgba(255,255,255, ${0.4 + 0.4 * frontness})`,
                                                    }}
                                                >
                                                    {card.paidAt}
                                                </span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}